import { useRef, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Tab,
  Table,
  Tabs
} from "react-bootstrap";
import {
  createDocumentGroup,
  replaceDocumentFile,
  updateDocumentGroup,
  updateDocumentMeta,
  uploadDocument
} from "../../controllers/documents";

const acceptedDocumentFileTypes = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation'
].join(',');

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentosAdminSection({ token, groups, onReload, onError, onSuccess }) {
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupLabel, setGroupLabel] = useState('');
  const [groupSortOrder, setGroupSortOrder] = useState(0);
  const [groupLoading, setGroupLoading] = useState(false);

  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docGroupId, setDocGroupId] = useState('');
  const [docTitle, setDocTitle] = useState('');
  const [docDescription, setDocDescription] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const fileInputRef = useRef();

  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replacingDoc, setReplacingDoc] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceLoading, setReplaceLoading] = useState(false);
  const replaceFileInputRef = useRef();

  function openCreateGroup() {
    setEditingGroup(null);
    setGroupLabel('');
    setGroupSortOrder(0);
    setShowGroupModal(true);
  }

  function openEditGroup(group) {
    setEditingGroup(group);
    setGroupLabel(group.label);
    setGroupSortOrder(group.sortOrder || 0);
    setShowGroupModal(true);
  }

  async function handleSaveGroup(e) {
    e.preventDefault();
    if (!groupLabel.trim()) return;
    setGroupLoading(true);
    try {
      if (editingGroup) {
        await updateDocumentGroup(token, editingGroup.id, { label: groupLabel, sortOrder: groupSortOrder });
        onSuccess('Grupo actualizado.');
      } else {
        await createDocumentGroup(token, groupLabel, groupSortOrder);
        onSuccess('Grupo creado.');
      }
      setShowGroupModal(false);
      await onReload();
    } catch (e2) {
      onError(e2.message || 'Error al guardar grupo.');
    } finally {
      setGroupLoading(false);
    }
  }

  async function toggleGroupActive(group) {
    try {
      await updateDocumentGroup(token, group.id, { active: !group.active });
      onSuccess(`Grupo ${group.active ? 'desactivado' : 'activado'}.`);
      await onReload();
    } catch (e) {
      onError('Error al cambiar estado del grupo.');
    }
  }

  function openUploadDoc(groupId) {
    setEditingDoc(null);
    setDocGroupId(String(groupId));
    setDocTitle('');
    setDocDescription('');
    setDocFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowDocModal(true);
  }

  function openEditDoc(doc) {
    setEditingDoc(doc);
    setDocGroupId(String(doc.groupId));
    setDocTitle(doc.title);
    setDocDescription(doc.description || '');
    setDocFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowDocModal(true);
  }

  async function handleSaveDoc(e) {
    e.preventDefault();
    if (!docTitle.trim() || !docGroupId) return;
    setDocLoading(true);
    try {
      if (editingDoc) {
        await updateDocumentMeta(token, editingDoc.id, {
          title: docTitle,
          description: docDescription,
          groupId: parseInt(docGroupId)
        });
        onSuccess('Documento actualizado.');
      } else {
        if (!docFile) {
          onError('Debe seleccionar un archivo.');
          setDocLoading(false);
          return;
        }
        await uploadDocument(token, docGroupId, docTitle, docDescription, docFile);
        onSuccess('Documento subido.');
      }
      setShowDocModal(false);
      await onReload();
    } catch (e2) {
      onError(e2.message || 'Error al guardar documento.');
    } finally {
      setDocLoading(false);
    }
  }

  async function toggleDocActive(doc) {
    try {
      await updateDocumentMeta(token, doc.id, { active: !doc.active });
      onSuccess(`Documento ${doc.active ? 'desactivado' : 'activado'}.`);
      await onReload();
    } catch (e) {
      onError('Error al cambiar estado del documento.');
    }
  }

  function openReplaceFile(doc) {
    setReplacingDoc(doc);
    setReplaceFile(null);
    if (replaceFileInputRef.current) replaceFileInputRef.current.value = '';
    setShowReplaceModal(true);
  }

  async function handleReplaceFile(e) {
    e.preventDefault();
    if (!replaceFile) return;
    setReplaceLoading(true);
    try {
      await replaceDocumentFile(token, replacingDoc.id, replaceFile);
      onSuccess('Archivo reemplazado.');
      setShowReplaceModal(false);
      await onReload();
    } catch (e2) {
      onError(e2.message || 'Error al reemplazar archivo.');
    } finally {
      setReplaceLoading(false);
    }
  }

  return (
    <>
      <div className="mt-3">
        <Row className="mb-3 align-items-center">
          <Col>
            <h5>Gestion de Grupos y Documentos</h5>
          </Col>
          <Col md="auto">
            <Button variant="success" onClick={openCreateGroup}>+ Nuevo Grupo</Button>
          </Col>
        </Row>

        {groups.length === 0 && (
          <p className="text-muted">No hay grupos de documentos. Crea uno para comenzar.</p>
        )}

        <Tabs defaultActiveKey={groups.length ? String(groups[0].id) : undefined} className="mb-3">
          {groups.map(group => (
            <Tab
              key={group.id}
              eventKey={String(group.id)}
              title={
                <span>
                  {group.label}
                  {!group.active && <Badge bg="secondary" className="ms-1">Inactivo</Badge>}
                </span>
              }
            >
              <Row className="mb-3 mt-3 align-items-center">
                <Col>
                  <h5>{group.label}</h5>
                  <span className="text-muted small">Orden: {group.sortOrder}</span>
                </Col>
                <Col md="auto" className="d-flex gap-2">
                  <Button size="sm" variant="outline-primary" onClick={() => openEditGroup(group)}>Editar grupo</Button>
                  <Button size="sm" variant={group.active ? 'outline-warning' : 'outline-success'} onClick={() => toggleGroupActive(group)}>
                    {group.active ? 'Desactivar' : 'Activar'}
                  </Button>
                  <Button size="sm" variant="primary" onClick={() => openUploadDoc(group.id)}>+ Subir documento</Button>
                </Col>
              </Row>

              <Table hover responsive>
                <thead>
                  <tr>
                    <th>Titulo</th>
                    <th>Archivo</th>
                    <th>Tamano</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {group.documents.length === 0 && (
                    <tr><td colSpan={5} className="text-muted text-center">Sin documentos.</td></tr>
                  )}
                  {group.documents.map(doc => (
                    <tr key={doc.id}>
                      <td>
                        <div>{doc.title}</div>
                        {doc.description && <div className="text-muted small">{doc.description}</div>}
                      </td>
                      <td>
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" className="small">
                          {doc.fileName}
                        </a>
                      </td>
                      <td className="small">{formatSize(doc.size)}</td>
                      <td>
                        <Badge bg={doc.active ? 'success' : 'secondary'}>
                          {doc.active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline-secondary" onClick={() => openEditDoc(doc)}>Editar</Button>
                          <Button size="sm" variant="outline-primary" onClick={() => openReplaceFile(doc)}>Reemplazar archivo</Button>
                          <Button size="sm" variant={doc.active ? 'outline-warning' : 'outline-success'} onClick={() => toggleDocActive(doc)}>
                            {doc.active ? 'Deshabilitar' : 'Habilitar'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Tab>
          ))}
        </Tabs>
      </div>

      <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)}>
        <Form onSubmit={handleSaveGroup}>
          <Modal.Header closeButton>
            <Modal.Title>{editingGroup ? 'Editar Grupo' : 'Nuevo Grupo'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre del grupo</Form.Label>
              <Form.Control
                type="text"
                value={groupLabel}
                onChange={e => setGroupLabel(e.target.value)}
                required
                placeholder="Ej: Reglamentos, Formas, etc."
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Orden de visualizacion</Form.Label>
              <Form.Control
                type="number"
                value={groupSortOrder}
                onChange={e => setGroupSortOrder(parseInt(e.target.value) || 0)}
                min={0}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowGroupModal(false)}>Cancelar</Button>
            <Button type="submit" disabled={groupLoading}>
              {groupLoading ? <Spinner size="sm" animation="border" /> : 'Guardar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDocModal} onHide={() => setShowDocModal(false)}>
        <Form onSubmit={handleSaveDoc}>
          <Modal.Header closeButton>
            <Modal.Title>{editingDoc ? 'Editar Documento' : 'Subir Documento'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Grupo</Form.Label>
              <Form.Select value={docGroupId} onChange={e => setDocGroupId(e.target.value)} required>
                <option value="">Seleccionar grupo...</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Titulo</Form.Label>
              <Form.Control
                type="text"
                value={docTitle}
                onChange={e => setDocTitle(e.target.value)}
                required
                placeholder="Nombre descriptivo del documento"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripcion (opcional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={docDescription}
                onChange={e => setDocDescription(e.target.value)}
              />
            </Form.Group>
            {!editingDoc && (
              <Form.Group>
                <Form.Label>Archivo</Form.Label>
                <Form.Control
                  type="file"
                  ref={fileInputRef}
                  onChange={e => setDocFile(e.target.files[0] || null)}
                  required
                  accept={acceptedDocumentFileTypes}
                />
                <Form.Text className="text-muted">PDF, imagenes, Word, Excel o PowerPoint. Max 20 MB.</Form.Text>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDocModal(false)}>Cancelar</Button>
            <Button type="submit" disabled={docLoading}>
              {docLoading ? <Spinner size="sm" animation="border" /> : 'Guardar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showReplaceModal} onHide={() => setShowReplaceModal(false)}>
        <Form onSubmit={handleReplaceFile}>
          <Modal.Header closeButton>
            <Modal.Title>Reemplazar Archivo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Reemplazando: <strong>{replacingDoc?.title}</strong></p>
            <p className="text-muted small">Archivo actual: {replacingDoc?.fileName}</p>
            <Form.Group>
              <Form.Label>Nuevo archivo</Form.Label>
              <Form.Control
                type="file"
                ref={replaceFileInputRef}
                onChange={e => setReplaceFile(e.target.files[0] || null)}
                required
                accept={acceptedDocumentFileTypes}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowReplaceModal(false)}>Cancelar</Button>
            <Button type="submit" variant="warning" disabled={replaceLoading}>
              {replaceLoading ? <Spinner size="sm" animation="border" /> : 'Reemplazar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export default DocumentosAdminSection;
